import React, { useMemo } from 'react';
import { QStack, QTag, QText } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { Tag } from '../../types';

type DefaultTagsDisplayProps = {
  label: string;
  tagIds: number[] | undefined;
  tags: Tag[];
};

/**
 * The "Default tags" row of the properties panel.
 *
 * Deliberately not built on LabelValueDisplay: that renders its value inside a
 * QText, and a wrapping flex row of chips is not valid content for a <p>. The
 * label markup is kept the same so the row still lines up with its neighbours.
 */
export const DefaultTagsDisplay: React.FC<DefaultTagsDisplayProps> = ({ label, tagIds, tags }) => {
  const { t } = useTranslation();

  const chips = useMemo(
    () =>
      tags
        .filter((tag) => tagIds?.includes(tag.id))
        .map((tag) => (
          <QTag
            key={tag.id}
            data-cy={`template-default-tag-${tag.name}`}
            // A tag belonging to groups is private: documents carrying it are
            // only visible to those groups.
            rightIcon={tag.group_ids?.length ? 'Lock' : undefined}
          >
            {tag.name}
          </QTag>
        )),
    [tagIds, tags],
  );

  return (
    <QStack spacing="1">
      <QText fontWeight="semibold">{label}</QText>
      {chips.length ? (
        <QStack
          data-cy="template-default-tags"
          direction="row"
          spacing="2"
          flexWrap="wrap"
          gap="8px"
          sx={{ span: { marginInlineStart: '0 !important' } }}
        >
          {chips}
        </QStack>
      ) : (
        <QText data-cy="template-default-tags">{t('common.none')}</QText>
      )}
    </QStack>
  );
};
